import { ApiError, handleApiError } from 'utils/api';
import { env } from '../../constants';
import {
  FormAnswers,
  GuestFormResponse,
  SaveFormAnswersRequest
} from './api-types';

const GUEST_FORMS_URL = `${env.VITE_API_BASE_URL}/guest/forms`;

/**
 * Map a failed guest response to a typed {@link ApiError} the runner turns into a screen.
 *
 * Same shape as the guest CV client, and for the same reasons: there is no Auth0 session to
 * recover, so a 401 never reloads — it surfaces as "session lapsed", which the autosave layer
 * repairs by re-redeeming the link that is still in the URL.
 *  - 401/403 → the form session lapsed or was killed (re-redeem, or a terminal screen)
 *  - 404     → the feature flag is off, or the route does not exist
 *  - 400     → structured server error ({ code, values }), e.g. error.form-answer.required
 */
const throwGuestFormError = async (response: Response): Promise<never> => {
  if (response.status === 401 || response.status === 403) {
    throw new ApiError({ code: 'error.guest.session-expired', values: {} });
  }
  if (response.status === 404) {
    throw new ApiError({ code: 'error.guest.unavailable', values: {} });
  }
  if (response.status === 400) {
    await handleApiError(response);
  }
  throw new ApiError({ code: 'error.guest.unknown', values: {} });
};

const unwrap = async <T>(response: Response): Promise<T> => {
  const json = (await response.json()) as { data: T };
  return json.data;
};

/**
 * Exchange the magic-link token for a session plus everything needed to render: the form's
 * definition, the answers saved so far, and whether it is already submitted.
 */
export const redeemGuestForm = async (
  linkToken: string
): Promise<GuestFormResponse> => {
  const response = await fetch(`${GUEST_FORMS_URL}/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: linkToken })
  });

  if (!response.ok) {
    await throwGuestFormError(response);
  }

  return unwrap<GuestFormResponse>(response);
};

/**
 * Autosave the whole answer set.
 *
 * `formRef` comes from the redeem response, never from the URL: the server checks that the path's
 * form matches the session's, so passing along a mistyped or truncated address would turn a
 * cosmetic URL problem into a hard mismatch error mid-form.
 */
export const saveGuestFormAnswers = async (
  formRef: string,
  sessionToken: string,
  answers: FormAnswers,
  signal?: AbortSignal
): Promise<GuestFormResponse> => {
  const payload: SaveFormAnswersRequest = { answers };
  const response = await fetch(`${GUEST_FORMS_URL}/${formRef}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    await throwGuestFormError(response);
  }

  return unwrap<GuestFormResponse>(response);
};

/** Finalize the form. Terminal: the invitation is consumed and the link stops working. */
export const submitGuestForm = async (
  formRef: string,
  sessionToken: string
): Promise<GuestFormResponse> => {
  const response = await fetch(`${GUEST_FORMS_URL}/${formRef}/submit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}` }
  });

  if (!response.ok) {
    await throwGuestFormError(response);
  }

  return unwrap<GuestFormResponse>(response);
};
